// Native RevenueCat billing adapter.
// No static `@revenuecat/purchases-capacitor` import — web CRA stays safe.
// Native shell talks to Capacitor.Plugins.Purchases after `npx cap sync`.

import {
    getIapProductById,
    getNoAdsProductId,
    listIapProducts,
} from "./iapCatalog";
import {
    getNativePlatform,
    getPurchasesPlugin,
    isNativeApp,
} from "./nativePlatform";
import {
    REVENUECAT_NO_ADS_ENTITLEMENT_ID,
    getRevenueCatApiKey,
    isRevenueCatKeyConfigured,
} from "./revenueCatConfig";

let configurePromise = null;
let cachedNativeProducts = [];

export function isRevenueCatBillingAvailable() {
    if (!isNativeApp()) {
        return false;
    }
    if (!isRevenueCatKeyConfigured(getNativePlatform())) {
        return false;
    }
    return Boolean(getPurchasesPlugin());
}

export function customerInfoHasNoAds(customerInfo) {
    const active = customerInfo?.entitlements?.active || {};
    return Boolean(
        active[REVENUECAT_NO_ADS_ENTITLEMENT_ID] ||
            active[getNoAdsProductId()]
    );
}

export function isPurchaseCancelledError(error) {
    if (!error) {
        return false;
    }
    if (error.userCancelled || error.code === "iap-cancelled") {
        return true;
    }
    const code = String(error.code || "");
    const message = String(error.message || "").toLowerCase();
    return (
        code.includes("PURCHASE_CANCELLED") ||
        code === "1" ||
        message.includes("cancelled") ||
        message.includes("canceled")
    );
}

function throwCancelled() {
    const error = new Error("Purchase cancelled.");
    error.code = "iap-cancelled";
    throw error;
}

function throwBillingUnavailable(reason) {
    const error = new Error(reason || "Native store billing is not ready.");
    error.code = "iap-not-ready";
    throw error;
}

export function buildCatalogGrant(product, { removeAds = null } = {}) {
    if (!product) {
        return {
            category: null,
            hearts: 0,
            treats: 0,
            packs: 0,
            boostBundle: 0,
            refillToMax: false,
            removeAds: Boolean(removeAds),
        };
    }

    return {
        category: product.category,
        hearts: Math.max(0, Math.floor(Number(product.hearts) || 0)),
        treats: Math.max(0, Math.floor(Number(product.treats) || 0)),
        packs: Math.max(0, Math.floor(Number(product.packs) || 0)),
        boostBundle: Math.max(0, Math.floor(Number(product.boostBundle) || 0)),
        refillToMax: Boolean(product.refillToMax),
        removeAds:
            removeAds == null ? Boolean(product.removeAds) : Boolean(removeAds),
    };
}

export async function configureRevenueCatBilling(appUserID = null) {
    if (!isRevenueCatBillingAvailable()) {
        return { configured: false, reason: "not-available" };
    }

    if (configurePromise) {
        return configurePromise;
    }

    configurePromise = (async () => {
        const Purchases = getPurchasesPlugin();
        const apiKey = getRevenueCatApiKey(getNativePlatform());
        const options = { apiKey };
        if (appUserID) {
            options.appUserID = String(appUserID);
        }
        await Purchases.configure(options);
        return { configured: true };
    })().catch((error) => {
        configurePromise = null;
        throw error;
    });

    return configurePromise;
}

export async function identifyRevenueCatUser(appUserID) {
    if (!isRevenueCatBillingAvailable()) {
        return null;
    }

    await configureRevenueCatBilling(appUserID);
    const Purchases = getPurchasesPlugin();
    if (!Purchases || !appUserID) {
        return null;
    }

    if (typeof Purchases.logIn !== "function") {
        return null;
    }

    const result = await Purchases.logIn({ appUserID: String(appUserID) });
    return result?.customerInfo || null;
}

export async function resetRevenueCatUser() {
    if (!isRevenueCatBillingAvailable()) {
        return;
    }

    const Purchases = getPurchasesPlugin();
    if (!Purchases || typeof Purchases.logOut !== "function") {
        return;
    }

    try {
        await Purchases.logOut();
    } catch (_) {
        // Anonymous restore still works after a failed logOut.
    }
}

async function ensureNativeProducts() {
    const Purchases = getPurchasesPlugin();
    if (!Purchases || typeof Purchases.getProducts !== "function") {
        return cachedNativeProducts;
    }

    const productIdentifiers = listIapProducts().map((product) => product.sku);
    const result = await Purchases.getProducts({
        productIdentifiers,
        type: "NON_SUBSCRIPTION",
    });
    cachedNativeProducts = Array.isArray(result?.products)
        ? result.products
        : [];
    return cachedNativeProducts;
}

function findNativeProduct(sku) {
    return (cachedNativeProducts || []).find(
        (item) => item && item.identifier === sku
    );
}

export async function fetchNativeStoreProducts() {
    if (!isRevenueCatBillingAvailable()) {
        return [];
    }

    await configureRevenueCatBilling();
    return ensureNativeProducts();
}

export async function purchaseNativeStoreProduct(productId) {
    const product = getIapProductById(productId);
    if (!product || product.enabled === false) {
        const error = new Error("Unknown or disabled store product.");
        error.code = "iap-unknown-product";
        throw error;
    }

    if (!isRevenueCatBillingAvailable()) {
        throwBillingUnavailable(
            "Native store billing is not ready. API keys still say Replace later, or the Purchases plugin is missing."
        );
    }

    await configureRevenueCatBilling();
    const Purchases = getPurchasesPlugin();
    if (!Purchases || typeof Purchases.purchaseStoreProduct !== "function") {
        throwBillingUnavailable("Purchases plugin is missing purchaseStoreProduct.");
    }

    let storeProduct = findNativeProduct(product.sku);
    if (!storeProduct) {
        await ensureNativeProducts();
        storeProduct = findNativeProduct(product.sku);
    }
    if (!storeProduct) {
        storeProduct = { identifier: product.sku };
    }

    try {
        const result = await Purchases.purchaseStoreProduct({
            product: storeProduct,
        });
        if (result?.userCancelled) {
            throwCancelled();
        }

        const customerInfo = result?.customerInfo || null;
        const removeAds =
            Boolean(product.removeAds) || customerInfoHasNoAds(customerInfo);

        return {
            productId: product.id,
            sku: product.sku,
            granted: true,
            isPreview: false,
            grant: buildCatalogGrant(product, { removeAds }),
            customerInfo,
        };
    } catch (error) {
        if (isPurchaseCancelledError(error)) {
            throwCancelled();
        }
        throw error;
    }
}

export async function restoreNativePurchases() {
    if (!isRevenueCatBillingAvailable()) {
        throwBillingUnavailable(
            "Native restore needs RevenueCat keys and the Purchases plugin."
        );
    }

    await configureRevenueCatBilling();
    const Purchases = getPurchasesPlugin();
    if (!Purchases || typeof Purchases.restorePurchases !== "function") {
        throwBillingUnavailable("Purchases plugin is missing restorePurchases.");
    }

    const result = await Purchases.restorePurchases();
    const customerInfo = result?.customerInfo || result;
    const restored = [];
    const noAdsProduct = getIapProductById(getNoAdsProductId());

    if (customerInfoHasNoAds(customerInfo)) {
        restored.push({
            productId: getNoAdsProductId(),
            sku: noAdsProduct?.sku || getNoAdsProductId(),
            grant: buildCatalogGrant(noAdsProduct, { removeAds: true }),
        });
    }

    return {
        restored,
        isPreview: false,
        customerInfo,
    };
}
